import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { Period } from "@/types/time.types";
import { BRUSSELS_TIMEZONE } from "@/constants/timezone.constants";
import { brusselsNow } from "@/utils/brussels-dayjs";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Checks whether a provided date is in the future
 * @param date - The date to check (Date object, string, or timestamp)
 * @returns true if the date is in the future, false otherwise
 * @example
 * ```typescript
 * isDateInFuture(new Date('2025-12-31')) // true
 * isDateInFuture('2023-01-01') // false
 * isDateInFuture(Date.now() + 86400000) // true (tomorrow)
 * ```
 */
export function isDateInFuture(date?: Date | string | number | null): boolean {
	if (!date) return false;

	const targetDate = dayjs(date);

	// dayjs has built-in validation
	if (!targetDate.isValid()) return false;

	// isAfter() compares with current time by default
	return targetDate.isAfter();
}

/**
 * Converts a Date object to ISO date format (YYYY-MM-DD) in Brussels timezone
 * @param date - The Date object to format
 * @returns Date string in ISO format (YYYY-MM-DD) in Brussels timezone
 * @example
 * ```typescript
 * const date = new Date('2025-08-11T10:45:00Z');
 * formatToISODate(date); // "2025-08-11" (in Brussels timezone)
 * ```
 */
export function formatToISODate(
	date: Date | string | null | undefined
): string {
	if (!date) return "";

	return dayjs(date).tz(BRUSSELS_TIMEZONE).format("YYYY-MM-DD");
}

/**
 * Validates whether a date is valid
 * @param date - The date to validate (Date object or string)
 * @returns true if the date is valid, false otherwise
 * @example
 * ```typescript
 * isValidDate(new Date()) // true
 * isValidDate('2025-08-11') // true
 * isValidDate('invalid-date') // false
 * isValidDate(new Date('invalid')) // false
 * ```
 */
export function isValidDate(date: string | Date): boolean {
	const isValid = new Date(date);
	return isValid instanceof Date && !Number.isNaN(isValid.getTime());
}

export function getUtcISOString(
	date: Date | string | null | undefined
): string {
	if (!date) return "";
	const d = dayjs(date);
	if (!d.isValid()) return "";

	return d.utc().format();
}

/**
 * Converts a date to start of day in Brussels timezone, returned as UTC ISO string.
 */
export function toStartOfDayUtc(
	date: Date | string | null | undefined
): string {
	if (!date) return "";
	const d = dayjs(date);
	if (!d.isValid()) return "";
	const dateString = d.tz(BRUSSELS_TIMEZONE).format("YYYY-MM-DD");
	return dayjs
		.tz(`${dateString}T00:00:00`, BRUSSELS_TIMEZONE)
		.utc()
		.toISOString();
}

/**
 * Converts a date to end of day in Brussels timezone, returned as UTC ISO string.
 */
export function toEndOfDayUtc(date: Date | string | null | undefined): string {
	if (!date) return "";
	const d = dayjs(date);
	if (!d.isValid()) return "";
	const dateString = d.tz(BRUSSELS_TIMEZONE).format("YYYY-MM-DD");
	return dayjs
		.tz(`${dateString}T23:59:59`, BRUSSELS_TIMEZONE)
		.utc()
		.toISOString();
}

/**
 * Converts a UTC date string to Brussels-local format for datetime-local inputs ("YYYY-MM-DDTHH:mm").
 */
export function toBrusselsDateTimeLocal(
	date: string | null | undefined
): string {
	if (!date) return "";
	return dayjs(date).tz(BRUSSELS_TIMEZONE).format("YYYY-MM-DDTHH:mm");
}

/**
 * An ISO 8601 string that already pins its own instant: a `Z`, or a numeric
 * offset like `+02:00` / `-0500`. A bare `2026-09-04` or `2026-09-04T16:30`
 * does not match — those are wall-clock digits with no instant attached.
 */
const HAS_EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Interprets a datetime string as Brussels time and returns UTC ISO string.
 * Use for precise datetimes (e.g. booking start/end times from forms).
 *
 * Input that already carries an offset is returned as-is. Such a string is a
 * fixed instant, not wall-clock digits, and reinterpreting it as Brussels time
 * silently moved it back by the offset — 1h CET, 2h CEST, never 0 here. That
 * shipped three times — end-service-now, card assignment starts, and the
 * scheduler's event window — because `new Date().toISOString()` and
 * `dayjs(x).toISOString()` both hand you exactly that shape. Passing through is
 * always the right answer: there is no case where re-reading the digits of an
 * absolute instant as local time is what the caller wanted.
 */
export function toUtcISOString(date: string | null | undefined): string {
	if (!date) return "";
	if (HAS_EXPLICIT_OFFSET.test(date)) return date;
	return dayjs.tz(date, BRUSSELS_TIMEZONE).utc().toISOString();
}

/**
 * Converts a  filter Period to an API-ready filter period DTO.
 * Start is always start-of-day Brussels in UTC.
 * End is always end-of-day Brussels in UTC.
 */
export function toFilterPeriodDto(period: Period) {
	return {
		start: period.start ? toUtcISOString(`${period.start}T00:00:00`) : "",
		end: period.end ? toUtcISOString(`${period.end}T23:59:59`) : "",
		timeZone: "BRUSSELS" as const,
	};
}

/** @deprecated Use `toStartOfDayUtc` instead */
export function toBrusselsStartOfDayUtc(
	date: Date | string | null | undefined
): string {
	if (!date) return "";
	const d = dayjs(date);
	if (!d.isValid()) return "";
	const dateString = d.format("YYYY-MM-DD");
	return dayjs
		.tz(`${dateString}T00:00:00`, BRUSSELS_TIMEZONE)
		.utc()
		.toISOString();
}

/** @deprecated Use `toEndOfDayUtc` instead */
export function toBrusselsEndOfDayUtc(
	date: Date | string | null | undefined
): string {
	if (!date) return "";
	const d = dayjs(date);
	if (!d.isValid()) return "";
	const dateString = d.format("YYYY-MM-DD");
	return dayjs
		.tz(`${dateString}T23:59:59`, BRUSSELS_TIMEZONE)
		.utc()
		.toISOString();
}

export function constructPeriodFromDateAndTime({
	fromDate,
	fromTime,
	toDate: toDateParameter,
	toTime,
}: {
	fromDate: string;
	fromTime: string;
	toDate: string;
	toTime: string;
}): Period | undefined {
	if (!fromDate || !fromTime || !toDateParameter || !toTime) return undefined;

	return {
		start: dayjs
			.tz(`${fromDate}T${fromTime}`, BRUSSELS_TIMEZONE)
			.utc()
			.toISOString(),
		end: dayjs
			.tz(`${toDateParameter}T${toTime}`, BRUSSELS_TIMEZONE)
			.utc()
			.toISOString(),
		timeZone: BRUSSELS_TIMEZONE,
	};
}

/**
 * Checks whether a provided date falls within the current week
 * @param date - The date to check (Date object, string, or timestamp)
 * @returns true if the date is within the current week, false otherwise
 * @example
 * ```typescript
 * isWithinCurrentWeek(new Date()) // true (today is always in current week)
 * isWithinCurrentWeek(new Date('2020-01-01')) // false (unless it's that week)
 * ```
 */
export function isWithinCurrentWeek(
	date?: Date | string | number | null
): boolean {
	if (!date) return false;

	const target = dayjs(date).tz(BRUSSELS_TIMEZONE);

	if (!target.isValid()) return false;

	return target.startOf("week").isSame(brusselsNow().startOf("week"));
}

/**
 * Gets the default period filter string for the current week
 * @returns Period string in format "YYYY-MM-DD_YYYY-MM-DD" (start_end of current week)
 * @example
 * ```typescript
 * getDefaultPeriodFilterString() // "2025-01-06_2025-01-12"
 * ```
 */
export function getDefaultPeriodFilterString(): string {
	const now = brusselsNow();
	return `${now.startOf("week").format("YYYY-MM-DD")}_${now.endOf("week").format("YYYY-MM-DD")}`;
}

/**
 * Strips the `[UTC]` or `[Europe/Brussels]` suffix from Web API date strings.
 * Some web API endpoints return dates in Java's ZonedDateTime format, e.g.
 * "2026-02-11T07:29:46.140575Z[UTC]" — the bracketed suffix is not valid
 * for JavaScript's Date constructor or dayjs, so it needs to be stripped.
 *
 * Only needed for the Web API. Fleet/Mobili APIs don't append this suffix.
 */
export function cleanWebApiDateString(
	dateString: string | null | undefined
): string | undefined {
	if (!dateString) return undefined;

	const cleaned = dateString.replace(/\[.*\]$/, "");
	const date = new Date(cleaned);

	if (Number.isNaN(date.getTime())) return undefined;

	return cleaned;
}

/** @deprecated Use `cleanWebApiDateString` instead */
export const parseWebApiDate = cleanWebApiDateString;

/**
 * Convert a date string to a Date object.
 * Use only at boundaries that require Date (scheduler, pickers, duration math).
 */
export function toDate(
	dateString: string | null | undefined
): Date | undefined {
	if (!dateString) return undefined;
	const cleaned = dateString.replace(/\[.*\]$/, "");
	const date = new Date(cleaned);
	return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * Formats a period (start + optional end) into a human-readable date string.
 * Defaults to Brussels timezone when none is provided.
 *
 * @param period - Object with start/end as Date or ISO string, and optional IANA timeZone
 * @returns Formatted period string, or null if start is missing/invalid
 * @example
 * ```typescript
 * formatPeriod({ start: new Date("2026-03-01T10:00:00Z"), end: new Date("2026-03-05T18:00:00Z") })
 * // "Mar 1, 2026 — Mar 5, 2026"
 * ```
 */
export function formatPeriod(period?: {
	start?: Date | string;
	end?: Date | string;
	timeZone?: string;
}): string | null {
	if (!period?.start) return null;

	const tz = period.timeZone ?? BRUSSELS_TIMEZONE;
	const start = dayjs(period.start).tz(tz);
	if (!start.isValid()) return null;

	const dateTimeFormat = "MMM D, YYYY HH:mm";
	const startFormatted = start.format(dateTimeFormat);

	if (!period.end) return startFormatted;

	const end = dayjs(period.end).tz(tz);
	if (!end.isValid()) return startFormatted;

	const sameDay = start.isSame(end, "day");
	const endFormat = sameDay ? "HH:mm" : dateTimeFormat;

	return `${startFormatted} — ${end.format(endFormat)}`;
}
